// Shared utility for accessing cached symbol names data
// Keys are symbol_id from source (underscore compound ids, e.g. AAPL_NYSE) -> company name

import { shouldUseFreshClientCache } from './clientCacheReload';

const TICKER_NAMES_CACHE_KEY = 'symbolNamesCache';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

// Track in-flight requests to coordinate multiple components
const inFlightRequest = { promise: null, timestamp: null, tradeDate: null };

const isNonEmptySymbolMap = (map) =>
  map && typeof map === 'object' && !Array.isArray(map) && Object.keys(map).length > 0;

/**
 * Infer symbol / exchange from compound symbol_id when only a legacy string value exists in cache.
 */
function inferFromSymbolId(symbolId) {
  if (!symbolId || typeof symbolId !== 'string') {
    return { symbol: '', exchange: '' };
  }
  const i = symbolId.indexOf('_');
  if (i < 0) return { symbol: symbolId, exchange: '' };
  return { symbol: symbolId.slice(0, i), exchange: symbolId.slice(i + 1) };
}

/**
 * Normalize API / cache entry to { symbolId, symbol, company_name, exchange }.
 * Values may be legacy plain company strings (symbolNameMap[id] === "Apple Inc.").
 */
export function normalizeSymbolNameEntry(symbolId, raw) {
  const id = symbolId != null ? String(symbolId) : '';
  if (raw == null || raw === undefined) {
    const inf = inferFromSymbolId(id);
    return { symbolId: id, symbol: inf.symbol, company_name: '', exchange: inf.exchange };
  }
  if (typeof raw === 'string') {
    const inf = inferFromSymbolId(id);
    return {
      symbolId: id,
      symbol: inf.symbol || id,
      company_name: raw,
      exchange: inf.exchange
    };
  }
  return {
    symbolId: id,
    symbol: raw.symbol != null && String(raw.symbol) !== '' ? String(raw.symbol) : inferFromSymbolId(id).symbol,
    company_name: raw.company_name || raw.name || '',
    exchange: raw.exchange != null ? String(raw.exchange) : ''
  };
}

/**
 * Get cached symbol names, using cache if available and valid
 * Coordinates requests so if a request was sent recently, all components wait for the same request
 * @param {string|null} tradeDate - Trade date to fetch symbols for (uses latest from cache if not provided)
 * @param {boolean} useCache - Whether to check cache first (default: true)
 * @returns {Promise<Object>} symbol_id → { company_name, symbol, exchange } (legacy: string company name only)
 */
export const getCachedSymbolNames = async (tradeDate = null, useCache = true) => {
  if (useCache && shouldUseFreshClientCache()) {
    useCache = false;
  }

  const now = Date.now();
  
  // Get trade date if not provided
  if (!tradeDate) {
    try {
      const { getCachedLatestTradeDate } = require('./tradeDatesCache');
      tradeDate = await getCachedLatestTradeDate('US');
      if (!tradeDate) {
        console.warn('[TICKER_NAMES CACHE] No trade date available, cannot fetch symbol names');
        return {};
      }
    } catch (error) {
      console.warn('[TICKER_NAMES CACHE] Error getting latest trade date:', error);
      return {};
    }
  }
  
  // STEP 1: Check for in-flight requests FIRST to coordinate simultaneous requests
  if (inFlightRequest.promise && inFlightRequest.tradeDate === tradeDate) {
    const requestAge = now - inFlightRequest.timestamp;
    // If request was made in last 5 seconds, wait for it
    if (requestAge < 5000) {
      console.log(`[TICKER_NAMES CACHE] Waiting for in-flight request (age: ${Math.round(requestAge / 1000)}s)`);
      try {
        const data = await inFlightRequest.promise;
        return data || {};
      } catch (error) {
        console.error(`[TICKER_NAMES CACHE] In-flight request failed:`, error);
        // Continue to make new request
      }
    }
  }
  
  // STEP 2: Check cache if enabled
  if (useCache) {
    try {
      const cachedData = localStorage.getItem(TICKER_NAMES_CACHE_KEY);
      if (cachedData) {
        const parsedCache = JSON.parse(cachedData);
        if (
          !parsedCache ||
          typeof parsedCache.timestamp !== 'number' ||
          typeof parsedCache.tradeDate !== 'string' ||
          !parsedCache.symbolNameMap ||
          typeof parsedCache.symbolNameMap !== 'object'
        ) {
          console.warn(`[TICKER_NAMES CACHE] Invalid cache shape, removing`);
          localStorage.removeItem(TICKER_NAMES_CACHE_KEY);
        } else {
        const cacheAge = now - parsedCache.timestamp;
        const cachedTradeDate = parsedCache.tradeDate;
        
        // Use cached data if it's less than 1 hour old and for the same trade date
        if (cacheAge < CACHE_TTL && cachedTradeDate === tradeDate && parsedCache.symbolNameMap) {
          console.log(`[TICKER_NAMES CACHE] Using cached data (age: ${Math.round(cacheAge / 1000)}s, tradeDate: ${tradeDate}, count: ${Object.keys(parsedCache.symbolNameMap).length})`);
          return parsedCache.symbolNameMap || {};
        } else {
          if (cacheAge >= CACHE_TTL) {
            console.log(`[TICKER_NAMES CACHE] Cache expired (age: ${Math.round(cacheAge / 1000)}s)`);
          } else if (cachedTradeDate !== tradeDate) {
            console.log(`[TICKER_NAMES CACHE] Cache for different trade date (cached: ${cachedTradeDate}, requested: ${tradeDate})`);
          }
          // Remove expired or mismatched cache
          localStorage.removeItem(TICKER_NAMES_CACHE_KEY);
        }
        }
      }
    } catch (error) {
      console.warn(`[TICKER_NAMES CACHE] Error reading cache:`, error);
      // Continue to fetch fresh data
    }
  }
  
  console.log(`[TICKER_NAMES CACHE] Starting new API request for trade date: ${tradeDate}`);
  
  const fetchPromise = (async () => {
    try {
      const response = await fetch(`/api/symbols-names/${tradeDate}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch symbol names: ${response.statusText}`);
      }
      const data = await response.json();
      
      console.log(`[TICKER_NAMES CACHE] Received ${Object.keys(data?.symbolNameMap || {}).length} symbols from server`);
      
      // Cache only non-empty data, so an empty API response does not overwrite a valid cache
      try {
        if (isNonEmptySymbolMap(data.symbolNameMap)) {
          const cacheData = {
            symbolNameMap: data.symbolNameMap,
            tradeDate: tradeDate,
            timestamp: Date.now()
          };
          localStorage.setItem(TICKER_NAMES_CACHE_KEY, JSON.stringify(cacheData));
          console.log(`[TICKER_NAMES CACHE] Saved to cache (${Object.keys(data.symbolNameMap || {}).length} symbols, tradeDate: ${tradeDate})`);
        } else {
          console.log(`[TICKER_NAMES CACHE] API returned empty symbol map, skipping cache write`);
        }
      } catch (cacheError) {
        console.warn(`[TICKER_NAMES CACHE] Error saving cache:`, cacheError);
        // Continue even if caching fails
      }
      
      return data.symbolNameMap || {};
    } catch (error) {
      console.error(`[TICKER_NAMES CACHE] Error fetching symbol names:`, error);
      throw error; // Re-throw so waiting components know it failed
    } finally {
      // Remove from in-flight requests after completion
      inFlightRequest.promise = null;
      inFlightRequest.timestamp = null;
      inFlightRequest.tradeDate = null;
      console.log(`[TICKER_NAMES CACHE] Request completed, removed from in-flight tracking`);
    }
  })();
  
  // Store the in-flight request with timestamp BEFORE making the request
  inFlightRequest.promise = fetchPromise;
  inFlightRequest.timestamp = now;
  inFlightRequest.tradeDate = tradeDate;
  
  try {
    const data = await fetchPromise;
    return data || {};
  } catch (error) {
    console.error(`[TICKER_NAMES CACHE] Fetch failed:`, error);
    // Return empty object on error
    return {};
  }
};

/**
 * Get cached symbol names synchronously (from cache only, no API call)
 * @returns {Object|null} Symbol names object or null if not cached/expired
 */
export const getCachedSymbolNamesSync = () => {
  try {
    const cachedData = localStorage.getItem(TICKER_NAMES_CACHE_KEY);
    if (cachedData) {
      const parsedCache = JSON.parse(cachedData);
      if (
        !parsedCache ||
        typeof parsedCache.timestamp !== 'number' ||
        !parsedCache.symbolNameMap ||
        typeof parsedCache.symbolNameMap !== 'object'
      ) {
        localStorage.removeItem(TICKER_NAMES_CACHE_KEY);
        return null;
      }
      const cacheAge = Date.now() - parsedCache.timestamp;
      
      // Use cached data if it's less than 1 hour old
      if (cacheAge < CACHE_TTL && isNonEmptySymbolMap(parsedCache.symbolNameMap)) {
        return parsedCache.symbolNameMap;
      }
    }
  } catch (error) {
    console.warn(`[TICKER_NAMES CACHE] Error reading cache:`, error);
  }
  
  return null;
};

/**
 * Get company name for a symbol_id (synchronous, from cache only)
 * @param {string} symbolId - symbol_id from source (exact key in map)
 * @returns {string|null} Company name or null if not found
 */
export const getCompanyName = (symbolId) => {
  if (!symbolId) return null;
  
  const symbolNameMap = getCachedSymbolNamesSync();
  if (symbolNameMap) {
    const direct = symbolNameMap[symbolId];
    if (direct != null) return typeof direct === 'string' ? direct : (direct.company_name || direct.name || null);
    const found = Object.keys(symbolNameMap).find(k => k.toUpperCase() === symbolId.toUpperCase());
    if (!found) return null;
    const v = symbolNameMap[found];
    return typeof v === 'string' ? v : (v.company_name || v.name || null);
  }
  
  return null;
};

/**
 * Clear the symbol names cache
 */
export const clearSymbolNamesCache = () => {
  try {
    localStorage.removeItem(TICKER_NAMES_CACHE_KEY);
    inFlightRequest.promise = null;
    inFlightRequest.timestamp = null;
    inFlightRequest.tradeDate = null;
    console.log(`[TICKER_NAMES CACHE] Cache cleared`);
  } catch (error) {
    console.warn(`[TICKER_NAMES CACHE] Error clearing cache:`, error);
  }
};

